import {
  Alert,
  AlertItem,
  Badge,
  Button,
  ContentArea,
  ContentContainer,
  MainContainer
} from '@vmware-react/clarity';
import React, { useCallback, useEffect, useState } from 'react';
import { NotificationApi } from 'vrlcm-gui-api';
import {
  HRow,
  InteractionBlocker,
  PageHeader,
  SpinnerWithText
} from 'vrlcm-gui-components';
import {
  LCMNotification,
  NotificationStateTypeConstants
} from 'vrlcm-gui-types';
import { absoluteRedirect, ErrorResponse, usePageTitle } from 'vrlcm-gui-utils';
import { pageSizeList } from '../../constants/notifications';
import NotificationList from './notification-list/NotificationList';
import { l10n } from 'i18n/i18nUtil';
import './Notifications.css';

const units = [
  {
    name: l10n('home'),
    to: '/'
  },
  {
    name: l10n('notifications.title'),
    to: '/notifications'
  }
];

interface Props {
  updatedNotificationByUser: () => void;
}

const Notifications: React.FC<Props> = props => {
  const { updatedNotificationByUser } = props;
  const [pageSize, setPageSize] = useState(pageSizeList[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const [notifications, setNotifications] = useState<LCMNotification[]>([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [markingNotificationAsRead, setMarkingNotificationAsRead] = useState(
    false
  );
  usePageTitle(l10n('notifications.title'));

  const [selected, setSelected] = useState<string[]>([]);

  const onSelectChange = useCallback(
    (newSelected: string[] = []) => setSelected(newSelected),
    []
  );

  const onDismissSuccessMessage = useCallback(
    () => setSuccessMessage(undefined),
    []
  );
  const onDismissErrorMessage = useCallback(
    () => setErrorMessage(undefined),
    []
  );

  const fetchAndUpdateNotifications = useCallback(
    (givenCurrentPage: number, givenPageSize: number) => {
      const cancelFunctions: Array<() => void> = [];
      setLoading(true);
      setErrorMessage(undefined);
      NotificationApi.fetchAllNotifications(
        givenCurrentPage,
        givenPageSize,
        cf => cancelFunctions.push(cf)
      ).then(
        res => {
          setTotalNotifications(res.totalElements);
          setLoading(false);
          setNotifications(res.content);
        },
        (err: ErrorResponse<{ message: string }>) => {
          setLoading(false);
          setErrorMessage(l10n('notifications.fetchFailed', [err.response.data.message]));
        }
      );
      return () => cancelFunctions.forEach(cf => cf());
    },
    []
  );

  useEffect(() => fetchAndUpdateNotifications(currentPage, pageSize), [
    fetchAndUpdateNotifications,
    currentPage,
    pageSize
  ]);

  const onVisitNotification = useCallback(
    (notification: LCMNotification) => {
      if (notification.state !== NotificationStateTypeConstants.READ) {
        setMarkingNotificationAsRead(true);
        NotificationApi.updateNotificationStatusAsRead(notification.vmid).then(
          () => {
            setMarkingNotificationAsRead(false);
            updatedNotificationByUser();
            if (notification.redirectUrl) {
              absoluteRedirect(notification.redirectUrl);
            }
          },
          () => {
            updatedNotificationByUser();
            setMarkingNotificationAsRead(false);
            if (notification.redirectUrl) {
              absoluteRedirect(notification.redirectUrl);
            }
          }
        );
      } else {
        if (notification.redirectUrl) {
          absoluteRedirect(notification.redirectUrl);
        }
      }
    },
    [updatedNotificationByUser]
  );

  const triggerListRefresh = useCallback(() => {
    if (currentPage !== 1) {
      setCurrentPage(1); // change in current page triggers fetch.
    } else {
      fetchAndUpdateNotifications(currentPage, pageSize);
    }
  }, [fetchAndUpdateNotifications, currentPage, pageSize]);

  const onClearSelected = useCallback(() => {
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    setClearing(true);
    Promise.all(
      selected.map(s => NotificationApi.updateNotificationStatusAsCleared(s))
    ).then(
      res => {
        updatedNotificationByUser();
        setSuccessMessage(l10n('notifications.clearSuccess', [l10n('selected')]));
        setClearing(false);
        setSelected([]);
        triggerListRefresh();
      },
      (err: ErrorResponse<{ message?: string }>) => {
        setErrorMessage(l10n('notifications.clearFailure', [err.response.data.message]));
        setClearing(false);
      }
    );
  }, [selected, triggerListRefresh, updatedNotificationByUser]);

  const onClearAll = useCallback(() => {
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    setClearing(true);
    NotificationApi.clearAllNotifications().then(
      res => {
        setSuccessMessage(l10n('notifications.clearSuccess', [l10n('all')]));
        setClearing(false);
        setSelected([]);
        triggerListRefresh();
      },
      (err: ErrorResponse<{ message?: string }>) => {
        setErrorMessage(l10n('notifications.clearFailure', [err.response.data.message]));
        setClearing(false);
      }
    );
  }, [triggerListRefresh]);

  return (
    <MainContainer className="shell">
      <ContentContainer>
        <ContentArea>
          <div className="notifications">
            <PageHeader
              breadcrumbUnits={units}
              actions={
                <>
                  {!clearing ? (
                    <>
                      {selected.length > 0 &&
                        selected.length !== totalNotifications && (
                          <Button
                            family="outline"
                            size="sm"
                            disabled={loading}
                            onClick={onClearSelected}>
                            <HRow marginRight={9}>
                              <span style={{ marginRight: '9px' }}>
                                <clr-icon shape="trash" />
                              </span>
                              <span>{l10n('notifications.clearSelected')}</span>
                              <Badge type={3}>{selected.length}</Badge>
                            </HRow>
                          </Button>
                        )}
                      {totalNotifications > 0 && (
                        <Button
                          class="danger"
                          size="sm"
                          disabled={loading}
                          onClick={onClearAll}>
                          <span style={{ marginRight: '9px' }}>
                            <clr-icon shape="trash" />
                          </span>
                          {l10n('notifications.clearAll')}
                        </Button>
                      )}
                    </>
                  ) : (
                    <SpinnerWithText size="sm">
                      {l10n('notifications.clearing')}
                    </SpinnerWithText>
                  )}
                </>
              }>
              <clr-icon
                aria-hidden="true"
                class="header-text-icon"
                shape="bell"
                size="24"
              />
              {l10n('notifications.title')}
            </PageHeader>
            <div>
              {errorMessage && (
                <Alert
                  type="danger"
                  closable={true}
                  onDismiss={onDismissErrorMessage}
                  ariaLiveMsg={errorMessage}
                  items={[<AlertItem>{errorMessage}</AlertItem>]}
                />
              )}
              {successMessage && (
                <Alert
                  type="success"
                  closable={true}
                  onDismiss={onDismissSuccessMessage}
                  ariaLiveMsg={successMessage}
                  items={[<AlertItem>{successMessage}</AlertItem>]}
                />
              )}
            </div>
            <InteractionBlocker
              disableInteraction={clearing || markingNotificationAsRead}
              overlaySpinnerMessage={
                markingNotificationAsRead ? l10n('notifications.pleaseWait') : undefined
              }>
              <NotificationList
                notifications={notifications}
                pageSize={pageSize}
                pageSizeChange={setPageSize}
                currentPage={currentPage}
                currentPageChange={setCurrentPage}
                loading={loading}
                totalNotifications={totalNotifications}
                selected={selected}
                selectedChange={onSelectChange}
                visitNotification={onVisitNotification}
              />
            </InteractionBlocker>
          </div>
        </ContentArea>
      </ContentContainer>
    </MainContainer>
  );
};

export default Notifications;
